import { useEffect, useRef } from 'react';
import { di } from 'react-magnetic-di';
import type { FieldValues } from 'state/persisted/request-create';
import { useAdjustedFields } from '../../common/fake-uim/store';

export interface Props {
    fieldValues: FieldValues;
    setFieldValue(field: string, value: string | null, shouldValidate?: boolean): void;
}

/**
 * `UiModificationsFormValuesApplier` applies UI Modification value overrides using the provided `setFieldValue` method and comparing with existing `fieldValues`.
 *
 * This components does not apply values visible for the user but only persists UI Modifications into the Formik state used in the RequestCreate experience.
 *
 * We assume that `setFieldValue` only accepts `string | null`, hence we only use the `basic` override here.
 *
 * @param props `fieldValues` and `setFieldValue` must be retrieved from the RequestCreate Formik's render props component.
 * @returns `null` Doesn't render anything visible
 */
export const UiModificationsFormValuesApplier = ({ fieldValues, setFieldValue }: Props) => {
    di(useAdjustedFields);

    const overrides = useAdjustedFields();
    const fieldValuesRef = useRef(fieldValues);

    // always refresh fieldValuesRef with the latest value
    fieldValuesRef.current = fieldValues;

    useEffect(() => {
        if (!overrides) {
            return;
        }

        for (const [fieldId, override] of Object.entries(overrides)) {
            const { value: adjustedFieldValue } = override;
            if (adjustedFieldValue && adjustedFieldValue.basic !== fieldValuesRef.current[fieldId]) {
                setFieldValue(fieldId, adjustedFieldValue.basic);
            }
        }
    }, [overrides, setFieldValue]);

    return null;
};
